import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@lib/api";
import { QUERY_KEY, UserData } from "./useGetUser";
import { type AutoTranslateLanguage } from "../types/StoryblokTypes";
import { API_ROUTES } from "../constants";

interface PreferencePayload {
  languagePreference?: string;
  themePreference?: string;
  filterPreference?: string[];
  autoTranslateEnabled?: boolean;
  autoTranslateLanguage?: AutoTranslateLanguage | null;
}

export const useUpdatePreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PreferencePayload) => {
      // fire and forget
      await apiClient.post(API_ROUTES.preference, payload);
    },
    onMutate: async (payload: PreferencePayload) => {
      // Cancel any outgoing fetches for get user
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY] });
      // Snapshot the previous value
      const previousPreference = queryClient.getQueryData<PreferencePayload>([
        QUERY_KEY,
      ]);

      // Optimistically update state
      // This will update the user preferences in the cache with the new values
      // It will not update the server, but it will make the UI feel snappier
      // and give the user immediate feedback
      queryClient.setQueryData([QUERY_KEY], (prevState: UserData) => {
        if (!prevState) {
          return payload;
        }

        const updatedState = {
          user: { ...prevState.user, ...payload },
          finishedWalkthroughs: prevState.finishedWalkthroughs ?? [],
        };
        return updatedState;
      });

      return { previousPreference };
    },
    onError: (
      error: unknown,
      _,
      context: { previousPreference?: PreferencePayload } | undefined
    ) => {
      console.error("Failed to update preference:", error);
      // rollback to previous state
      if (context?.previousPreference) {
        queryClient.setQueryData([QUERY_KEY], context.previousPreference);
      }
    },
    // refetch after mutation
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
